import { Crisp } from "crisp-sdk-web";
import environment from "@/constants/Environment";
import { userSlice } from "@/redux/User";
import * as GtmService from "@/services/gtmService";

const environmentData = environment();

export type AuthSessionData = {
  userData: any;
  companyData: any;
  defaultCompany: boolean;
  isKeyValid?: boolean;
  creditUsed?: number;
};

export type TwoFactorChallengeState = {
  challengeId?: string;
  email?: string;
  prevPath?: string;
  setupRequired?: boolean;
  companyName?: string;
  setupData?: {
    secret?: string;
    base32Secret?: string;
    manualEntryKey?: string;
    otpAuthUrl?: string;
    otpauthUrl?: string;
    uri?: string;
    qrCode?: string;
    qrCodeUrl?: string;
    qrCodeDataUrl?: string;
    qrCodeImage?: string;
    qr?: string;
  };
};

const CHALLENGE_STORAGE_KEY = "twoFactorLoginChallenge";

export const getStoredTwoFactorChallenge = (): TwoFactorChallengeState => {
  try {
    return JSON.parse(
      sessionStorage.getItem(CHALLENGE_STORAGE_KEY) ?? "{}",
    ) as TwoFactorChallengeState;
  } catch {
    return {};
  }
};

export const saveTwoFactorChallenge = (challenge: TwoFactorChallengeState) => {
  sessionStorage.setItem(CHALLENGE_STORAGE_KEY, JSON.stringify(challenge));
};

export const clearTwoFactorChallenge = () => {
  sessionStorage.removeItem(CHALLENGE_STORAGE_KEY);
};

export const getPendingTwoFactorChallenge = () => {
  const challenge = getStoredTwoFactorChallenge();
  return challenge.challengeId ? challenge : null;
};

export const isTwoFactorRequired = (data: any) => {
  const authData = data?.data ?? data ?? {};
  return Boolean(
    authData.requiresTwoFactor ??
    authData.twoFactorRequired ??
    authData.requires2FA ??
    authData.mfaRequired,
  );
};

export const getTwoFactorChallenge = (data: any) => {
  const authData = data?.data ?? data ?? {};
  return {
    challengeId:
      authData.challengeId ??
      authData.twoFactorChallengeId ??
      authData.mfaToken ??
      authData.token,
    email: authData.email ?? authData.userEmail,
    setupRequired: Boolean(
      authData.twoFactorSetupRequired ?? authData.setupRequired,
    ),
    companyName: authData.companyName,
    setupData: authData.setupData,
  };
};

export function applyAuthenticatedSession({
  authData,
  dispatch,
}: {
  authData: AuthSessionData;
  dispatch: any;
}) {
  const { userData, companyData, defaultCompany, isKeyValid, creditUsed } =
    authData;

  if (environmentData.isAI) {
    window.ChatbotBuilder?.setUserData({
      name: userData.userName,
      email: userData.userEmail,
      plan: companyData.ai_subscription?.plan?.name ?? "",
      plan_status: companyData.ai_subscription?.plan?.status ?? "",
      isLoggedIn: "true",
      aiModel: companyData.companyModel,
    });
    window.ChatbotBuilder?.resetChat();
  } else {
    Crisp.setTokenId(userData._id);
    Crisp.session.reset();
    Crisp.user.setEmail(userData.userEmail);
  }

  GtmService.pushToDataLayer({
    event: "sign_in",
    user_id: userData._id,
    user_email: userData.userEmail,
    company_id: companyData._id,
    company_name: companyData.compName,
    sub_domain: companyData.subDomain,
  });

  dispatch(
    userSlice.actions.login({
      userId: userData._id,
      userEmail: userData.userEmail,
      userName: userData.userName,
      userPhone: userData.userPhone,
      hasSetPassword: userData?.hasSetPassword ?? true,
      sessionCount: userData.sessionCount,
      defaultCompany,
      companyDetails: {
        _id: companyData._id,
        compName: companyData.compName,
        userId: companyData.userId,
        fromGemini: companyData.fromGemini,
        fromMu: companyData?.fromMu,
        count: companyData.count,
        subDomain: companyData.subDomain,
        createdAt: companyData.createdAt,
        updatedAt: companyData.updatedAt,
        planName: companyData.ai_subscription?.plan?.name ?? "",
        companyModel: companyData.companyModel,
        defaultCustomDomain: companyData.defaultCustomDomain,
      },
      showCoupon: userData.showCoupon ?? true,
    }),
  );
  dispatch(userSlice.actions.setCreditId(companyData.creditId));
  dispatch(userSlice.actions.setIsNewUser(companyData.isNewUser));
  dispatch(userSlice.actions.setByok(!!companyData.byok));
  dispatch(userSlice.actions.setCreditUsed(creditUsed ?? 0));

  if (isKeyValid) {
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message: "Your plan upgraded successfully",
      }),
    );
  }
}
